/**
 * groupItemsBy — Sprint 2 (FE-9.4).
 *
 * Pure functions that take a flat `EnhancedInventoryItem[]` and
 * produce sections for the Browse screen's group-by layouts.
 *
 * Five layouts share one helper signature:
 *   groupItemsBy(mode, items, [groups]) → BrowseSection[]
 *
 *  - 'recent'    — single section "Recent" containing the input array
 *                  unchanged. (Recent is really a degenerate case;
 *                  the Browse screen actually uses the existing
 *                  FlatList for this mode and skips the helper.)
 *  - 'scenes'    — group by `source_file_id`. Section title is
 *                  "Scene N" (or item count). Hero photo = the
 *                  group's `source_image_url` (first non-null).
 *  - 'location'  — group by `location` field. Items with no location
 *                  fall into an "Unsorted" bucket at the END.
 *  - 'crew'      — sectioned by group (crews). Caller must provide
 *                  the list of `GroupListItem`. We DON'T look up
 *                  per-item membership here (Gap A in the contract
 *                  reference) — instead the caller passes
 *                  pre-grouped item arrays via `crewSections`.
 *  - 'tag'       — group by first tag (comma-split). Items with no
 *                  tags fall into an "Untagged" bucket at the END.
 *
 * The helper is pure, deterministic, and dependency-free so it
 * fully unit-tests without React or Redux.
 */

import type {
  EnhancedInventoryItem,
  GroupListItem,
} from '../../api/types';

export type BrowseLayout =
  | 'recent'
  | 'scenes'
  | 'location'
  | 'crew'
  | 'tag';

export interface BrowseSection {
  /** Stable key for FlatList/SectionList — must be unique per render. */
  key: string;
  /** Display title above the section. */
  title: string;
  /** Optional subtitle (e.g. "3 members · Calgary"). */
  subtitle?: string;
  /** Optional hero photo URL (Scenes layout uses this). */
  heroPhotoUrl?: string;
  /** When the section header is tappable (Crew "Open crew →"). */
  href?: { kind: 'group'; groupId: number };
  /** Items in the section. */
  data: EnhancedInventoryItem[];
}

/* ── helpers ─────────────────────────────────────────────────────── */

/** Split a tags string ("kitchen,utility, foo") into trimmed items.
 *  Returns [] for null/empty/whitespace-only. */
export function parseTags(raw: string | null | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[,;]/)
    .map((t) => t.trim())
    .filter(Boolean);
}

/* ── main entry ──────────────────────────────────────────────────── */

export interface GroupItemsByOptions {
  /** For 'crew' mode — the user's crews. Order in the output mirrors
   *  this list, with PERSONAL pinned to the top. */
  crews?: GroupListItem[];

  /** For 'crew' mode — pre-fetched per-crew inventory. Keyed by
   *  group_id. Caller is responsible for fetching these (the helper
   *  is pure). Omitted crews render as empty sections with a CTA to
   *  load. */
  crewItemsById?: Record<number, EnhancedInventoryItem[]>;
}

export function groupItemsBy(
  mode: BrowseLayout,
  items: EnhancedInventoryItem[],
  options: GroupItemsByOptions = {},
): BrowseSection[] {
  switch (mode) {
    case 'recent':
      return items.length === 0
        ? []
        : [{ key: 'recent', title: 'Recent', data: items }];

    case 'scenes':
      return groupByScenes(items);

    case 'location':
      return groupByLocation(items);

    case 'crew':
      return groupByCrew(items, options);

    case 'tag':
      return groupByTag(items);
  }
}

/* ── scenes ──────────────────────────────────────────────────────── */

function groupByScenes(items: EnhancedInventoryItem[]): BrowseSection[] {
  // Map source_file_id → items, preserving insertion order so the
  // most-recent-scanned scene shows first (matches the order
  // selectFilteredInventoryItems delivers).
  const buckets = new Map<number, EnhancedInventoryItem[]>();
  let unknownIdx = -1;
  for (const it of items) {
    const id = it.source_file_id ?? --unknownIdx;
    if (!buckets.has(id)) buckets.set(id, []);
    buckets.get(id)!.push(it);
  }

  const out: BrowseSection[] = [];
  let n = 0;
  for (const [fileId, group] of buckets) {
    n += 1;
    // First non-null source_image_url in the group becomes the hero.
    const hero = group.find((g) => !!g.source_image_url)?.source_image_url;
    const isUnknown = fileId < 0;
    out.push({
      key: `scene-${fileId}`,
      title: isUnknown ? 'Manually added' : `Snapshot ${n}`,
      subtitle:
        group.length === 1 ? '1 item' : `${group.length} items`,
      heroPhotoUrl: hero,
      data: group,
    });
  }
  return out;
}

/* ── location ────────────────────────────────────────────────────── */

function groupByLocation(items: EnhancedInventoryItem[]): BrowseSection[] {
  const buckets = new Map<string, EnhancedInventoryItem[]>();
  const unsorted: EnhancedInventoryItem[] = [];
  for (const it of items) {
    const loc = (it.location ?? '').trim();
    if (!loc) {
      unsorted.push(it);
    } else {
      if (!buckets.has(loc)) buckets.set(loc, []);
      buckets.get(loc)!.push(it);
    }
  }

  const out: BrowseSection[] = [];
  // Stable alphabetical order for named locations
  for (const loc of [...buckets.keys()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  )) {
    const group = buckets.get(loc)!;
    out.push({
      key: `loc-${loc}`,
      title: loc,
      subtitle: group.length === 1 ? '1 item' : `${group.length} items`,
      data: group,
    });
  }
  // Unsorted always last
  if (unsorted.length > 0) {
    out.push({
      key: 'loc-unsorted',
      title: 'Unsorted',
      subtitle:
        unsorted.length === 1 ? '1 item' : `${unsorted.length} items`,
      data: unsorted,
    });
  }
  return out;
}

/* ── crew ────────────────────────────────────────────────────────── */

function groupByCrew(
  _items: EnhancedInventoryItem[],
  options: GroupItemsByOptions,
): BrowseSection[] {
  const crews = options.crews ?? [];
  if (crews.length === 0) return [];

  // Personal first, then non-personal in their natural order.
  const sorted = [...crews].sort((a, b) => {
    const ap = a.kind === 'PERSONAL' ? 0 : 1;
    const bp = b.kind === 'PERSONAL' ? 0 : 1;
    if (ap !== bp) return ap - bp;
    return a.name.localeCompare(b.name);
  });

  const out: BrowseSection[] = [];
  for (const crew of sorted) {
    const items = options.crewItemsById?.[crew.id] ?? [];
    out.push({
      key: `crew-${crew.id}`,
      title: crew.name,
      subtitle: subtitleForCrew(crew, items.length),
      href: { kind: 'group', groupId: crew.id },
      data: items,
    });
  }
  return out;
}

function subtitleForCrew(crew: GroupListItem, loadedCount: number): string {
  const items =
    typeof crew.item_count === 'number'
      ? crew.item_count === 1
        ? '1 item'
        : `${crew.item_count} items`
      : loadedCount === 1
      ? '1 item'
      : `${loadedCount} items`;
  if (typeof crew.member_count === 'number' && crew.member_count > 1) {
    return `${items} · ${crew.member_count} members`;
  }
  return items;
}

/* ── tag ─────────────────────────────────────────────────────────── */

function groupByTag(items: EnhancedInventoryItem[]): BrowseSection[] {
  const buckets = new Map<string, EnhancedInventoryItem[]>();
  const untagged: EnhancedInventoryItem[] = [];
  for (const it of items) {
    const tags = parseTags(it.tags);
    if (tags.length === 0) {
      untagged.push(it);
    } else {
      const first = tags[0];
      if (!buckets.has(first)) buckets.set(first, []);
      buckets.get(first)!.push(it);
    }
  }

  const out: BrowseSection[] = [];
  for (const tag of [...buckets.keys()].sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' }),
  )) {
    const group = buckets.get(tag)!;
    out.push({
      key: `tag-${tag}`,
      title: tag,
      subtitle: group.length === 1 ? '1 item' : `${group.length} items`,
      data: group,
    });
  }
  if (untagged.length > 0) {
    out.push({
      key: 'tag-untagged',
      title: 'Untagged',
      subtitle:
        untagged.length === 1 ? '1 item' : `${untagged.length} items`,
      data: untagged,
    });
  }
  return out;
}
